import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { ArrowLeft, Check, X } from "lucide-react";
import { logger } from "../helpers/logHandler";
import { logoutProcessor } from "../helpers/logoutProcessor";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import FitWord from "../FitWord";
import { apiFetch } from "../../api/apiFetch";
import { zoneDispatchReasonKey } from "./zoneDispatchReasons";
import "../../styles/scansheet.css";

type megaSorter2 = {
    warehouse: string,
    bay: string,
    lane: string,
    batch: number,
    date: string,
    resetFunc: () => void,
    setLane: React.Dispatch<React.SetStateAction<string>>
}

const ZoneStorageSorter2MegaOrder = ({ warehouse, bay, lane, batch, date, resetFunc, setLane }: megaSorter2) => {
    const navigator = useNavigate();
    const { t } = useTranslation();
    const [scan, set_scan] = useState<string>("");
    const [tag, set_tag] = useState<string>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    // One tote pre-check / one close in flight at a time.
    const busyRef = useRef(false);

    // Handle tag scan. With the warehouse's Sorter 2 → Portal dispatch switch
    // on, the BE is asked whether the tote is free before the tag is accepted
    // (the mega order's number is only resolved server-side at close, so
    // this is a tote-only check). Switch off, or a network failure, accepts
    // the tag as before — the close re-checks everything under locks.
    const handle_tag_scan = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.slice(0, 3) === "NPS" || val.slice(0, 3) === "PLS" || val.length > 8 || val === "") {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("invalid_tag"));
            set_scan("");
            return;
        }
        set_scan("");
        if (busyRef.current) return;
        busyRef.current = true;
        let refusal: { reason?: string; status?: string } | null = null;
        try {
            const res = await apiFetch(`/zonestorage/orders/check-tag`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, tag: val, mode: "sorter_2" }),
            });
            if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
            if (res.ok) {
                const data = await res.json();
                if (data && data.ok === false) refusal = data;
            }
        } catch {
            refusal = null;
        } finally {
            busyRef.current = false;
        }
        if (refusal) {
            flashScanStatus("error");
            playError();
            toast.dismiss();
            toast.error(t(zoneDispatchReasonKey(refusal.reason), { status: refusal.status ?? "", order: "" }));
            logger(warehouse, "S2 Mega Order", String(lane), 0, val, "Open Tag", "N/A", refusal.reason ?? "refused", "error");
            return;
        }
        set_tag(val);
    }, [scan, warehouse, lane, navigator, t]);

    // Handle confirmation
    const handle_confirmation = async (answer: boolean) => {
        if (answer === false) {
            set_tag("");
            return;
        }
        if (busyRef.current) return;
        busyRef.current = true;
        try {
            const res = await apiFetch(`/zonestorage/orders/create_mega_order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ warehouse: warehouse, date: date, batch: batch, lane: lane, tag: tag })
            });
            if (res.ok) {
                console.log("Order Created");
                // Every unit in the lane was missing at sort: Portal flagged
                // the order for a leader and bound no tote (the lane is
                // still closed). A plain close answers an empty body.
                let data: any = null;
                try { data = await res.json(); } catch { data = null; }
                if (data && typeof data === "object" && data.flagged === true) {
                    toast.dismiss();
                    toast.info(t("zone_order_flagged_missing", { order: data.order_id ?? "" }));
                    logger(warehouse, "S2 Mega Order", String(lane), 0, tag, "Close Tag", "N/A", "flagged_missing", "success");
                }
                setLane("");
                return;
            }
            if (res.status === 401) {
                console.log("INVALID PERMISSIONS");
                logoutProcessor();
                navigator("/login");
                return;
            }
            let response: any = null;
            try { response = await res.json(); } catch { response = null; }
            console.log(response);
            playError();
            flashScanStatus("error");
            toast.dismiss();
            if (response && typeof response === "object" && response.reason) {
                // A Portal-dispatch refusal (tote in use, order missing / not
                // ready / short of stock, items don't match). Name it and drop
                // back to the tag prompt so a different tote can be scanned or
                // a supervisor can sort the order out.
                toast.error(t(zoneDispatchReasonKey(response.reason), { status: response.status ?? "", order: "" }));
                logger(warehouse, "S2 Mega Order", String(lane), 0, tag, "Close Tag", "N/A", response.reason, "error");
                set_tag("");
            } else {
                toast.error(t("scan_failed"));
            }
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            busyRef.current = false;
        }
    };

    // Reset lane locally and on API before resetting the rest
    const reset_preface = async () => {
        // Ask API to reset lane
        await apiFetch(`/zonestorage/lanes/change-in-use-status`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, lane: lane, bay: bay, inUse: false, user: "" })
        }).then(async (res): Promise<any> => {
            const response = await res.json();
            if (res.ok) {
                console.log("Complete");
            } else if (res.status === 401) {
                console.log("INVALID PERMISSIONS");
                logoutProcessor();
                navigator("/login");
            } else {
                playError();
                console.log(response.message);
                return;
            }
        });

        setLane("");
        resetFunc();
    };

    return (
        <div className="scan-panel">
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("mega_order")}</h1>
                <button className="page-back-btn" onClick={reset_preface}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {/* Green success tick removed — keep the red X (error). */}
                <ScanStatusIcons status={scanStatus === "success" || scanStatus === "error" ? "idle" : scanStatus} />

                <div className="scan-feedback-area">
                    <h2 className="no-margin">{t("lane_label", { lane: String(lane) })}</h2>
                    <h2 className="no-margin">{t("tag_label", { tag })}</h2>
                </div>

                {tag === "" && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("scan_tag_prompt").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}

                {tag != "" && (
                    <div className={`scan-feedback-area ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        <h2>{t("details_correct")}</h2>
                        <div className="conveyor-btn-group">
                            <button className="conveyor-yes-btn" onClick={(e) => { e.preventDefault(); handle_confirmation(true); }}><Check size={24} strokeWidth={3} />{t("yes")}</button>
                            <button className="conveyor-no-btn" onClick={(e) => { e.preventDefault(); handle_confirmation(false); }}><X size={24} strokeWidth={3} />{t("no")}</button>
                        </div>
                    </div>
                )}
            </div>
            {tag === "" && (
                <HiddenScanInput value={scan} onChange={set_scan} onSubmit={handle_tag_scan} disabled={false} disableCamera />
            )}
        </div>
    )
};

export default ZoneStorageSorter2MegaOrder;
